import React, { type FC, useCallback } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useCrossFlow, Journeys } from '@atlassiansox/cross-flow-support/cross-flow-api-internals';
import { Targets } from '@atlassiansox/cross-flow-support/cross-flow-base-types';
import { Behaviours, Prompts, TargetTabs } from '@atlassiansox/cross-flow-extensions/types';
import { createProvisioningApiExtensions } from '@atlassiansox/cross-flow-extensions/ProvisioningApiExtensions';

import { JiraProductDiscoveryIcon } from '@atlaskit/logo/jira-product-discovery/icon';
import Lozenge from '@atlaskit/lozenge/lozenge';
import {
	ButtonMenuItem,
	type ButtonMenuItemProps,
} from '@atlaskit/side-nav-items/button-menu-item';

import type { CustomItemProps } from '@atlassian/navigation-common/types';

import { useConfluenceAnalyticsEvents } from '@confluence/analytics/entry-points/useConfluenceAnalyticsEvents.ts';

import type { MenuId } from '../../../PlatformNavigationTypes.ts';

import { useJpdAccessCapability } from './useJpdAccessCapability.ts';

const i18n = defineMessages({
	jpdName: {
		id: 'side-navigation.app-shortcuts.try-jpd.shortcut-name',
		description:
			'Label for the Jira Product Discovery app shortcut shown in the sidebar to users who do not have a JPD license.',
		defaultMessage: 'Jira Product Discovery',
	},
	tryLozenge: {
		id: 'side-navigation.app-shortcuts.try-jpd.try-lozenge',
		description:
			'Text shown inside a lozenge on the right of the Jira Product Discovery shortcut, inviting the user to try the product.',
		defaultMessage: 'Try',
	},
});

type GlobalItemProps = {
	isHidden?: boolean;
	peekingId?: MenuId;
	setPeekingId?: (menuId?: MenuId) => void;
};

export type TryJPDItemProps = CustomItemProps & GlobalItemProps;

const JPD_TEMPLATE_ID = 'jpd.product-roadmap';
const JPD_CROSS_FLOW_EXTENSIONS = createProvisioningApiExtensions({
	actions: {
		primary: {
			behaviour: Behaviours.LAND,
			prompt: Prompts.NEXT,
			options: {
				targetTab: TargetTabs.SAME,
				urlParams: { templateId: JPD_TEMPLATE_ID },
			},
		},
	},
	jobName: 'activate-and-ensure-project-created',
	jobParams: {
		sourceComponent: 'confluenceSideNav',
		sourceContext: 'tryJpdShortcut',
	},
});

const useOpenJpdCrossFlow = (): (() => void) => {
	const crossFlow = useCrossFlow();

	return useCallback(() => {
		if (!crossFlow.isEnabled) {
			return;
		}

		void crossFlow.apiV1.open({
			journey: Journeys.GET_STARTED,
			targetProduct: Targets.JIRA_PRODUCT_DISCOVERY,
			sourceComponent: 'appsNavigationGrowthItem',
			sourceContext: 'appsNavigationGrowthItem',
			extensions: JPD_CROSS_FLOW_EXTENSIONS,
			v1: true,
		});
	}, [crossFlow]);
};

export const TryJPDItem: FC<TryJPDItemProps> = ({ onClick, isHidden, ...rest }) => {
	const { formatMessage } = useIntl();
	const { createEvent } = useConfluenceAnalyticsEvents();
	const accessCapability = useJpdAccessCapability();
	const openJpdCrossFlow = useOpenJpdCrossFlow();

	// Legacy navigation props must not be passed to ButtonMenuItem.
	const { peekingId: _peekingId, setPeekingId: _setPeekingId, draggableRef, ...dndProps } = rest;

	const elemBefore: ButtonMenuItemProps['elemBefore'] = <JiraProductDiscoveryIcon size="xsmall" />;

	const elemAfter: ButtonMenuItemProps['elemAfter'] = (
		<Lozenge appearance="new">{formatMessage(i18n.tryLozenge)}</Lozenge>
	);

	const onMenuItemClick: ButtonMenuItemProps['onClick'] = () => {
		createEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'navigationItem',
				actionSubjectId: 'tryJpdShortcut',
				source: 'globalNavigation',
				attributes: {
					accessCapability,
					navigationLayer: 'global',
					navigationContainer: 'sidebar',
				},
			},
		}).fire();
		onClick?.();
		openJpdCrossFlow();
	};

	if (isHidden) {
		return null;
	}

	return (
		<ButtonMenuItem
			ref={draggableRef}
			aria-haspopup="dialog"
			elemAfter={elemAfter}
			elemBefore={elemBefore}
			onClick={onMenuItemClick}
			{...dndProps}
		>
			<FormattedMessage {...i18n.jpdName} />
		</ButtonMenuItem>
	);
};
